/**
 * 머리글 도구줄 접기/펼치기 — 좁은 화면(휴대폰) 전용(TASK_mobile_header_collapse).
 *
 * 실측(2026-10-14, iPhone 390pt 뷰어): 머리글+도구줄이 화면 높이의 약 40%
 * (227px/844px)를 먹었다. 참가자는 휴대폰으로 들어오고(§14) 그 화면의
 * 주인공은 자막이다 — 도구줄은 **기본 접힘**, 필요할 때 펼친다.
 *
 * 규율:
 *  · 좁은 화면 판정은 `SplitFeed` 와 **같은 문턱**(≤640px)이다. 문턱이 둘이
 *    되면 그 사이 폭에서 어긋난 조합이 생긴다(split-feed.tsx 와 같은 이유).
 *  · 넓은 화면은 현행 유지 — `narrow=false` 면 항상 펼친 것으로 본다.
 *  · 접힘/펼침은 **기기별 취향**이라 localStorage 에 기억한다. 저장이 막혀도
 *    (프라이빗 모드·방어적 getter) 메모리 상태로 동작한다(use-feed-order.ts
 *    와 같은 규약, try 안에서 typeof 검사까지 한다).
 *  · 기능은 숨기되 제거하지 않는다 — 펼치면 모든 버튼이 그대로 선다.
 */

import { useCallback, useEffect, useState } from 'react'

/** `SplitFeed`(split-feed.tsx BREAKPOINT_QUERY)와 같은 값 — 둘이 갈라지면 안 된다. */
export const HEADER_NARROW_QUERY = '(max-width: 640px)'

export type HeaderRole = 'host' | 'viewer'

export function headerToolsStorageKey(role: HeaderRole): string {
  return `omnitrans:header-tools:${role}`
}

function isNarrow(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(HEADER_NARROW_QUERY).matches
}

function loadExpanded(role: HeaderRole): boolean {
  try {
    if (typeof localStorage === 'undefined') return false
    return localStorage.getItem(headerToolsStorageKey(role)) === 'open'
  } catch {
    return false
  }
}

export interface HeaderTools {
  /** 좁은 화면인가(≤640px). 넓은 화면에서는 토글을 그리지 않는다. */
  narrow: boolean
  /** 사용자가 펼쳐 둔 상태인가(좁은 화면에서만 의미가 있다). 기본 false. */
  expanded: boolean
  /** 도구줄을 그려야 하는가 — 넓은 화면이거나, 좁은 화면에서 펼친 경우. */
  showTools: boolean
  toggle: () => void
}

export function useHeaderTools(role: HeaderRole): HeaderTools {
  const [narrow, setNarrow] = useState(isNarrow)
  const [expanded, setExpanded] = useState(() => loadExpanded(role))

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mql = window.matchMedia(HEADER_NARROW_QUERY)
    const onChange = (): void => setNarrow(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev
      try {
        localStorage.setItem(headerToolsStorageKey(role), next ? 'open' : 'closed')
      } catch {
        // 저장 실패는 무시한다 — 접힘/펼침은 이번 세션만 유지된다.
      }
      return next
    })
  }, [role])

  return { narrow, expanded, showTools: !narrow || expanded, toggle }
}
